/**
 * The subset of the Passgenau IdP contract the portal consumes.
 *
 * These mirror `api/openapi.yaml` in the passgenau-idp repository. The portal
 * uses the backend-facilitated login endpoints (`/auth/*`) rather than the
 * OAuth redirect flow, so it never handles an upstream authorization code and
 * never holds a client secret.
 *
 * There is no local password store: every sign-in is federated, either through
 * one of the platform's own registrations or through the customer's own.
 */

/** Wire shape of `TokenResponse`. Snake case because it is RFC 6749's. */
export interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  refresh_token?: string | null
  id_token?: string | null
  scope?: string | null
}

/** An upstream the IdP can federate to. */
export type FederatedProvider = 'microsoft' | 'google'

/** One upstream registration from `GET /auth/provider`. */
export interface AuthProviderSummary {
  /** Path-safe name to use in `/auth/{provider}/url`, e.g. `microsoft`. */
  provider: string
  /** Label for the sign-in button. */
  displayName?: string | null
  /**
   * False when the deployment names the provider but has not finished
   * configuring it. Such a provider must not be offered: starting it fails.
   */
  enabled: boolean
}

/**
 * Result of `GET /auth/provider`: the platform's own registrations only.
 *
 * Per-customer connections are deliberately absent — listing them would let
 * anyone enumerate which companies use the platform. An address reaches its own
 * connection through `discoverLoginMethod` instead.
 */
export interface AuthProviderList {
  providers: AuthProviderSummary[]
}

/** One way a given address may sign in. */
export interface LoginMethod {
  /** `microsoft` or `google`. */
  provider: string
  /**
   * Targets this customer's own app registration. Absent when the method is one
   * of the platform's default registrations.
   */
  connectionId?: string | null
  /** Label for the sign-in button, e.g. "Contoso (Microsoft)". */
  displayName?: string | null
}

/**
 * Result of `POST /auth/discovery`, in the order the page should offer them.
 *
 * A domain with its own connection yields exactly one entry; any other address
 * yields the platform's default registrations. An unrecognised domain is
 * therefore answered the same way as an unconfigured one, so this cannot be
 * used to enumerate customers. An empty list means nothing is configured at all.
 */
export interface DiscoveryResponse {
  methods: LoginMethod[]
}

export interface ConsentUrlResponse {
  url: string
  state: string
}

/**
 * The platform view of the identity, resolved by the IdP from the portal
 * database. The same values populate the Hasura claims in the access token.
 */
export interface PlatformClaims {
  defaultRole?: string | null
  allowedRoles?: string[] | null
  userId?: string | null
  companyId?: string | null
  companyName?: string | null
  companyIdentifier?: string | null
  primaryDomain?: string | null
  tenantId?: string | null
}

/**
 * Result of `GET /auth/session`. The portal reads the identity from here
 * rather than decoding the ID token client-side.
 */
export interface Session {
  sub: string
  email: string
  name?: string | null
  picture?: string | null
  connection?: string | null
  amr?: string[] | null
  authTime?: number | null
  platform?: PlatformClaims
}

/** Machine-readable failure from the IdP's `Error` schema. */
export class IdpError extends Error {
  readonly status: number
  readonly code: string
  readonly requestId: string | undefined

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message)
    this.name = 'IdpError'
    this.status = status
    this.code = code
    this.requestId = requestId
  }

  /** The session cannot be recovered without signing in again. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }
}
